import React, { useState, useCallback } from "react";
import * as FlexLayout from "flexlayout-react";
import Uoms from "./uoms";
import Uom from "./uom";
import UomTypes from "./uomTypes";
import UomType from "./uomType";
import { useTranslation } from "react-i18next";

const UomAdmin = ({ allowEdit }) => {
  const { t } = useTranslation(["markets"]);

  // Define initial layout
  const initialLayout = {
    global: {
      minWidth: 100,
      minHeight: 100,
    },
    borders: [],
    layout: {
      type: "row",
      children: [
        {
          type: "tabset",
          weight: 50,
          enableMaximize: false,
          children: [
            {
              type: "tab",
              name: t("Uoms"),
              enableClose: false,
              component: "Uoms",
            },
            {
              type: "tab",
              name: t("UomTypes"),
              enableClose: false,
              component: "UomTypes",
            },
          ],
        },
        {
          type: "tabset",
          id: "1",
          weight: 50,
          enableDeleteWhenEmpty: false,
          enableMaximize: false,
          children: [],
        },
      ],
    },
  };

  // Create model state
  const [model] = useState(() => FlexLayout.Model.fromJson(initialLayout));

  // Handle UOM selection
  const handleUomSelect = useCallback(
    (uom, submitCallbackFunction, isNew) => {
      const { uomId, name } = uom;

      try {
        model.doAction(
          FlexLayout.Actions.addNode(
            {
              type: "tab",
              component: "Uom",
              name: `${name}`,
              id: `${uomId}`,
              config: {
                uomId,
                submitCallbackFunction,
                isNew,
              },
            },
            "1",
            FlexLayout.DockLocation.CENTER,
            0,
            true
          )
        );
      } catch (error) {
        // Tab already exists, select it
        model.doAction(FlexLayout.Actions.selectTab(uomId));
      }
    },
    [model]
  );

  // Handle UOM Type selection
  const handleUomTypeSelect = useCallback(
    (uomType, submitCallbackFunction, isNew) => {
      const { uomTypeId, name } = uomType;

      try {
        model.doAction(
          FlexLayout.Actions.addNode(
            {
              type: "tab",
              component: "UomType",
              name: `${name}`,
              id: `${uomTypeId}`,
              config: {
                uomTypeId,
                submitCallbackFunction,
                isNew,
              },
            },
            "1",
            FlexLayout.DockLocation.CENTER,
            0,
            true
          )
        );
      } catch (error) {
        // Tab already exists, select it
        model.doAction(FlexLayout.Actions.selectTab(uomTypeId));
      }
    },
    [model]
  );

  // Handle tab rename
  const handleTabRename = useCallback(
    (nodeId, name) => {
      model.doAction(
        FlexLayout.Actions.updateNodeAttributes(nodeId, { name: name })
      );
    },
    [model]
  );

  // Component factory function
  const factory = useCallback(
    (node) => {
      const componentId = node.getComponent();

      if (componentId === "Uoms") {
        return <Uoms onClick={handleUomSelect} allowEdit={allowEdit} />;
      } else if (componentId === "Uom") {
        const { uomId, submitCallbackFunction, isNew } = node.getConfig();
        return (
          <Uom
            uomId={uomId}
            submitCallbackFunction={submitCallbackFunction}
            isNew={isNew}
            onRename={handleTabRename}
            allowEdit={allowEdit}
          />
        );
      } else if (componentId === "UomTypes") {
        return <UomTypes onClick={handleUomTypeSelect} allowEdit={allowEdit} />;
      } else if (componentId === "UomType") {
        const { uomTypeId, submitCallbackFunction, isNew } = node.getConfig();
        return (
          <UomType
            uomTypeId={uomTypeId}
            submitCallbackFunction={submitCallbackFunction}
            isNew={isNew}
            onRename={handleTabRename}
            allowEdit={allowEdit}
          />
        );
      } else if (componentId === "Placeholder") {
        return <div></div>;
      } else {
        return null;
      }
    },
    [allowEdit, handleUomSelect, handleUomTypeSelect, handleTabRename]
  );

  return (
    <div className="flex-container">
      <FlexLayout.Layout model={model} factory={factory} />
    </div>
  );
};

export default UomAdmin;
